import React from "react";
import styled from "styled-components";
import { FiMousePointer } from "react-icons/fi";
import { colors } from "./colors";
import { formatDayYear, formatNumber, formatWeekdayYear } from "./format";
import { metricOf, metricDelta, toneColors } from "./metrics";
import { weekEndOf } from "./traffic";

export default function TrafficTooltip({ active, payload, metric, weekly, events, changeDays }) {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    const config = metricOf(metric);
    const end = weekly ? weekEndOf(row.date) : row.date;
    const inBucket = (date) => date >= row.date && date <= end;
    const updates = events.filter((event) => inBucket(event.date));
    const changeCount = changeDays.filter((day) => inBucket(day.date)).reduce((total, day) => total + day.changeCount, 0);
    const delta = row.previous !== null ? metricDelta(metric, row.current, row.previous) : null;

    return (
        <Box>
            <Heading>{weekly ? `Week of ${formatDayYear(row.date)}` : formatWeekdayYear(row.date)}</Heading>
            <List>
                <Line>
                    <Term><Swatch $color={colors.accent} />{config.label}</Term>
                    <Strong>{config.formatPrecise(row.current)}</Strong>
                </Line>
                {row.previous !== null && row.previousDate && (
                    <Line>
                        <Term><Swatch $dashed />{formatDayYear(row.previousDate)}</Term>
                        <Muted>{config.formatPrecise(row.previous)}</Muted>
                    </Line>
                )}
                {delta && (
                    <Line $separated>
                        <Term>Change</Term>
                        <Strong style={{ color: toneColors[delta.tone], fontWeight: 500 }}>{delta.text}</Strong>
                    </Line>
                )}
            </List>
            {(updates.length > 0 || changeCount > 0) && (
                <Events>
                    {updates.map((event) => (
                        <EventLine key={event.id}><Bullet $color={colors.algorithm} />{event.title}</EventLine>
                    ))}
                    {changeCount > 0 && <EventLine><Bullet $color={colors.pageChange} />{formatNumber(changeCount)} page changes detected</EventLine>}
                </Events>
            )}
            {changeCount > 0 && <Hint><FiMousePointer aria-hidden />Click to filter drops by these changes</Hint>}
        </Box>
    );
}

const Box = styled.div`
    min-width: 210px;
    border-radius: 8px;
    border: 1px solid ${colors.line};
    background: ${colors.surface};
    padding: 10px 12px;
    font-size: 12px;
    box-shadow: 0 10px 15px -3px rgba(15, 20, 25, 0.1);
`;

const Heading = styled.p`
    margin: 0;
    font-weight: 500;
    color: ${colors.ink};
`;

const List = styled.dl`
    margin: 8px 0 0;
`;

const Line = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 6px;
    ${({ $separated }) => $separated && `border-top: 1px solid ${colors.line}; padding-top: 6px;`}
`;

const Term = styled.dt`
    display: flex;
    align-items: center;
    gap: 6px;
    color: ${colors.muted};
`;

const Swatch = styled.span`
    width: 12px;
    ${({ $dashed, $color }) => ($dashed ? `border-top: 1px dashed ${colors.subtle};` : `height: 2px; border-radius: 2px; background: ${$color};`)}
`;

const Strong = styled.dd`
    margin: 0;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${colors.ink};
`;

const Muted = styled.dd`
    margin: 0;
    font-variant-numeric: tabular-nums;
    color: ${colors.muted};
`;

const Events = styled.ul`
    margin: 8px 0 0;
    padding: 8px 0 0;
    list-style: none;
    border-top: 1px solid ${colors.line};
`;

const EventLine = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 4px;
    font-weight: 500;
    color: ${colors.ink};
`;

const Bullet = styled.span`
    margin-top: 4px;
    width: 6px;
    height: 6px;
    flex-shrink: 0;
    border-radius: 999px;
    background: ${({ $color }) => $color};
`;

const Hint = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 8px 0 0;
    color: ${colors.subtle};
`;
